import i18n from '@/i18n'
import { cloneValue, getAtPath, setAtPath } from './configPaths'
import { imageFieldReferences } from './schemaV2'
import { validateBrowserAsset } from './assets'
import { cropAroundFocus, cropForTarget, readImageDimensions, targetForProfile } from './imageCrops'
import { backTransparentLogo } from './logoBacking'

// Generated URLs are evidence, not draft-owned uploads. Use the same import and
// pending-file registration as the image picker before exposing a savable result.
export async function importGenerationAssets(template, configuration, {
  ownedAssets = {}, pendingFiles = {}, fetchAsset, addAsset, signal, readDimensions = readImageDimensions, logoImport = null,
}) {
  let next = cloneValue(configuration)
  const imports = new Map()
  const references = []
  for (const reference of imageFieldReferences(template.schema, next)) {
    const media = getAtPath(next, reference.path)
    const sourceId = media?.sourceAssetId
    if (!sourceId || pendingFiles[sourceId]) continue
    if (ownedAssets[sourceId]) {
      next.assets[sourceId] = cloneValue(ownedAssets[sourceId])
      continue
    }
    const source = next.assets?.[sourceId]
    const url = source?.url
    if (!url || !/^https?:\/\//i.test(url)) throw new Error(i18n.t('editorV2.generation.errors.imageNoSource'))
    if (!imports.has(url)) imports.set(url, { source, sourceId, profiles: [] })
    imports.get(url).profiles.push(template.manifest.assetProfiles?.[reference.profile])
    references.push({ ...reference, media, sourceId, url })
  }
  // Prepare every download before mutating editor state. Cancellation or a failed
  // download must not leave a partly applied composition.
  for (const [url, entry] of imports) {
    signal?.throwIfAborted()
    entry.file = await fetchAsset(url, signal)
    for (const profile of entry.profiles) {
      const error = validateBrowserAsset(entry.file, profile?.mimeTypes)
      if (error) throw new Error(i18n.t('editorV2.generation.errors.imageImport', { error }))
    }
    if (references.some((ref) => ref.url === url && ref.path === 'data.brand.logo')) {
      const prepared = await backTransparentLogo(entry.file, logoImport)
      entry.file = prepared.file
      entry.transformation = prepared.transformation
    }
    if (entry.profiles.some((profile) => profile?.mode === 'crop')) {
      entry.dimensions = await readDimensions(entry.file)
    }
  }
  signal?.throwIfAborted()
  for (const entry of imports.values()) {
    entry.pendingId = addAsset(entry.file)
    if (!entry.pendingId) throw new Error(i18n.t('editorV2.generation.errors.imageRegister'))
    next.assets = { ...next.assets, [entry.pendingId]: {
      ...entry.source, id: entry.pendingId,
      sourceEvidence: { sourceId: entry.sourceId, sourceUrl: entry.source.url, transformation: entry.transformation || 'import', version: 1 },
    } }
  }
  for (const { path, profile, media, sourceId, url } of references) {
    const entry = imports.get(url)
    const id = entry.pendingId
    const target = targetForProfile(template.manifest, profile, next)
    const needsCrop = target?.mode === 'crop'
    const crop = needsCrop ? (media.focalPoint
      ? cropAroundFocus(entry.dimensions, target, media.focalPoint)
      : cropForTarget(media.crop, entry.dimensions, target)) : null
    next = setAtPath(next, path, { ...media, sourceAssetId: id, renditionAssetId: needsCrop ? null : id, crop })
    delete next.assets[sourceId]
    for (const key of ['imageCandidates', 'logoCandidates', 'textInImage']) {
      if (Array.isArray(next.suggestions?.[key])) {
        next.suggestions[key] = [...new Set(next.suggestions[key].map((candidate) => candidate === sourceId ? id : candidate))]
      }
    }
  }
  return next
}
